import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

export enum WorkflowID {
  OFFICE_ACTION = 'officeAction',
  LITIGATION = 'litigation',
  PATENT_DRAFT = 'patentDraft',
  GENERAL_ASSISTANT = 'generalAssistant',
  INVENTION_ASSISTANT = 'inventionAssistant',
}

export interface IBaseTab {
  id: string;
  name: string;
  type: string;
  isLoading?: boolean;
}

export interface IPanel<TTab extends IBaseTab = IBaseTab> {
  id: string;
  name: string;
  tabs: TTab[];
  activeTabId: string | null;
  tabHistory: string[];
  order?: number;
  width?: number;
}

export interface ITabDragSession {
  tabId: string;
  panelId: string;
  width: number;
  grabOffsetX: number;
}

interface IWorkflowPanelState {
  panels: IPanel[];
  activeDragItem: { name: string } | null;
  hoveredPanelId: string | null;
  lastInteractedEditorDocumentId: string | null;
  tabDragSession: ITabDragSession | null;
}

interface IPanelSliceState {
  workflows: Record<string, IWorkflowPanelState>;
}

const addTabToHistory = (
  currentHistory: string[],
  currentActiveTabId: string | null,
  newActiveTabId: string,
  maxHistorySize: number = 10,
): string[] => {
  if (!currentActiveTabId || currentActiveTabId === newActiveTabId) {
    return currentHistory;
  }
  return [currentActiveTabId, ...currentHistory.filter(id => id !== currentActiveTabId)].slice(0, maxHistorySize);
};

const cleanupTabHistory = (currentHistory: string[], availableTabIds: string[], tabIdToRemove?: string): string[] => {
  return currentHistory.filter(
    historyTabId => historyTabId !== tabIdToRemove && availableTabIds.includes(historyTabId),
  );
};

const addOrUpdateTabInPanel = (panels: IPanel[], targetPanelId: string, tabToAdd: IBaseTab): IPanel[] => {
  return panels.map(panel => {
    if (panel.id !== targetPanelId) return panel;

    const existingTab = panel.tabs.find(t => t.id === tabToAdd.id);

    if (existingTab) {
      return {
        ...panel,
        tabs: panel.tabs.map(t => (t.id === existingTab.id ? { ...existingTab, ...tabToAdd, id: existingTab.id } : t)),
        activeTabId: existingTab.id,
        tabHistory: addTabToHistory(panel.tabHistory, panel.activeTabId, existingTab.id),
      };
    }

    return {
      ...panel,
      tabs: [...panel.tabs, tabToAdd],
      activeTabId: tabToAdd.id,
      tabHistory: addTabToHistory(panel.tabHistory, panel.activeTabId, tabToAdd.id),
    };
  });
};

const removeTabFromPanel = (panel: IPanel, tabId: string): IPanel => {
  const updatedTabs = panel.tabs.filter(tab => tab.id !== tabId);
  const updatedHistory = cleanupTabHistory(
    panel.tabHistory,
    updatedTabs.map(t => t.id),
    tabId,
  );
  const newActiveTabId =
    panel.activeTabId === tabId ? updatedHistory[0] || updatedTabs[0]?.id || null : panel.activeTabId;

  return {
    ...panel,
    tabs: updatedTabs,
    activeTabId: newActiveTabId,
    tabHistory: updatedHistory,
  };
};

const initialState: IPanelSliceState = { workflows: {} };

const ensureWorkflow = (state: IPanelSliceState, workflowId: string): IWorkflowPanelState => {
  if (!state.workflows[workflowId]) {
    state.workflows[workflowId] = {
      panels: [],
      activeDragItem: null,
      hoveredPanelId: null,
      lastInteractedEditorDocumentId: null,
      tabDragSession: null,
    };
  }
  return state.workflows[workflowId];
};

const getTabDocumentId = (tab: IBaseTab | undefined): string | null => {
  if (!tab || !('documentId' in tab)) return null;
  const { documentId } = tab;
  return typeof documentId === 'string' && documentId ? documentId : null;
};

const panelSlice = createSlice({
  name: 'panelSlice',
  initialState,
  reducers: {
    createPanel(
      state,
      action: PayloadAction<{ workflowId: string; panelId: string; name?: string; nextPanelNumber: number }>,
    ) {
      const { workflowId, panelId, name, nextPanelNumber } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels.push({
        id: panelId,
        name: name ?? `Panel ${nextPanelNumber}`,
        tabs: [],
        activeTabId: null,
        tabHistory: [],
        order: workflow.panels.length,
      });
    },

    removePanel(state, action: PayloadAction<{ workflowId: string; panelId: string }>) {
      const workflow = ensureWorkflow(state, action.payload.workflowId);
      const filteredPanels = workflow.panels.filter(panel => panel.id !== action.payload.panelId);
      workflow.panels = filteredPanels.map((panel, index) => ({ ...panel, order: index }));
    },

    clearPanels(state, action: PayloadAction<{ workflowId: string }>) {
      ensureWorkflow(state, action.payload.workflowId).panels = [];
    },

    restorePanels(state, action: PayloadAction<{ workflowId: string; panels: IPanel[] }>) {
      ensureWorkflow(state, action.payload.workflowId).panels = action.payload.panels;
    },

    openTabInPanel(
      state,
      action: PayloadAction<{ workflowId: string; panelId: string; tab: IBaseTab; updatedPanels?: IPanel[] }>,
    ) {
      const { workflowId, panelId, tab, updatedPanels } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = updatedPanels ?? addOrUpdateTabInPanel(workflow.panels, panelId, tab);

      const documentId = getTabDocumentId(tab);
      if (documentId) {
        workflow.lastInteractedEditorDocumentId = documentId;
      }
    },

    closeTabInPanel(state, action: PayloadAction<{ workflowId: string; panelId: string; tabId: string }>) {
      const { workflowId, panelId, tabId } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels
        .map(panel => (panel.id === panelId ? removeTabFromPanel(panel, tabId) : panel))
        .filter(panel => panel.tabs.length > 0);
    },

    setActivePanelTab(state, action: PayloadAction<{ workflowId: string; panelId: string; tabId: string }>) {
      const { workflowId, panelId, tabId } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);

      const documentId = getTabDocumentId(
        workflow.panels.find(panel => panel.id === panelId)?.tabs.find(tab => tab.id === tabId),
      );
      if (documentId) {
        workflow.lastInteractedEditorDocumentId = documentId;
      }

      workflow.panels = workflow.panels.map(panel => {
        if (panel.id !== panelId) return panel;
        return {
          ...panel,
          activeTabId: tabId,
          tabHistory: [tabId, ...panel.tabHistory.filter(id => id !== tabId)],
        };
      });
    },

    moveTabToPanel(
      state,
      action: PayloadAction<{
        workflowId: string;
        sourcePanelId: string;
        targetPanelId: string;
        tab: IBaseTab;
        updatedPanels?: IPanel[];
      }>,
    ) {
      const { workflowId, sourcePanelId, targetPanelId, tab, updatedPanels } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.tabDragSession = null;

      const panelsWithoutTab = (updatedPanels ?? workflow.panels)
        .map(panel => (panel.id === sourcePanelId ? removeTabFromPanel(panel, tab.id) : panel))
        .filter(panel => panel.id !== sourcePanelId || panel.tabs.length > 0);
      workflow.panels = addOrUpdateTabInPanel(panelsWithoutTab, targetPanelId, tab);
    },

    moveTabToNewPanel(
      state,
      action: PayloadAction<{
        workflowId: string;
        sourcePanelId: string;
        tab: IBaseTab;
        newPanelId: string;
        newPanelName: string;
        nextPanelNumber: number;
      }>,
    ) {
      const { workflowId, sourcePanelId, tab, newPanelId, newPanelName, nextPanelNumber } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.tabDragSession = null;

      const panelsWithoutTab = workflow.panels
        .map(panel => (panel.id === sourcePanelId ? removeTabFromPanel(panel, tab.id) : panel))
        .filter(panel => panel.tabs.length > 0);

      workflow.panels = [
        ...panelsWithoutTab,
        {
          id: newPanelId,
          name: newPanelName || `Panel ${nextPanelNumber}`,
          tabs: [tab],
          activeTabId: tab.id,
          tabHistory: [tab.id],
          order: workflow.panels.length,
        },
      ];
    },

    reorderTabsInPanel(
      state,
      action: PayloadAction<{ workflowId: string; panelId: string; reorderedTabs: IBaseTab[] }>,
    ) {
      const { workflowId, panelId, reorderedTabs } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels.map(panel => {
        if (panel.id !== panelId) return panel;
        return { ...panel, tabs: reorderedTabs };
      });
    },

    reorderOrMoveTab(
      state,
      action: PayloadAction<{
        workflowId: string;
        sourcePanelId: string;
        targetPanelId: string;
        tab: IBaseTab;
        targetTabId: string;
        position: 'left' | 'right';
      }>,
    ) {
      const { workflowId, sourcePanelId, targetPanelId, tab, targetTabId, position } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.tabDragSession = null;

      // Same-panel reorder: handle in a single pass to avoid remove-then-reinsert stale data
      if (sourcePanelId === targetPanelId) {
        workflow.panels = workflow.panels.map(panel => {
          if (panel.id !== sourcePanelId) return panel;

          const sourceIndex = panel.tabs.findIndex(t => t.id === tab.id);
          if (sourceIndex === -1) return panel;

          const targetIndex = panel.tabs.findIndex(t => t.id === targetTabId);
          if (targetIndex === -1) return panel;

          const newTabs = [...panel.tabs];
          const [movedTab] = newTabs.splice(sourceIndex, 1);
          const effectiveTargetIndex = newTabs.findIndex(t => t.id === targetTabId);
          const insertIndex = position === 'left' ? effectiveTargetIndex : effectiveTargetIndex + 1;
          newTabs.splice(insertIndex, 0, movedTab);

          return { ...panel, tabs: newTabs };
        });
        return;
      }

      // Cross-panel move: remove from source, insert into target
      const updatedPanels = workflow.panels
        .map(panel => (panel.id === sourcePanelId ? removeTabFromPanel(panel, tab.id) : panel))
        .filter(panel => panel.tabs.length > 0);

      workflow.panels = updatedPanels.map(panel => {
        if (panel.id !== targetPanelId) return panel;

        const targetIndex = panel.tabs.findIndex(t => t.id === targetTabId);
        const effectiveTargetIndex = targetIndex === -1 ? (position === 'left' ? 0 : panel.tabs.length) : targetIndex;
        const insertIndex = position === 'left' ? effectiveTargetIndex : effectiveTargetIndex + 1;
        const newTabs = [...panel.tabs];
        newTabs.splice(insertIndex, 0, { ...tab });

        return {
          ...panel,
          tabs: newTabs,
          activeTabId: tab.id,
          tabHistory: [tab.id, ...panel.tabHistory],
        };
      });
    },

    reorderPanels(state, action: PayloadAction<{ workflowId: string; newOrder: string[] }>) {
      const { workflowId, newOrder } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = newOrder
        .map((panelId, index) => {
          const panel = workflow.panels.find(p => p.id === panelId);
          return panel ? { ...panel, order: index } : null;
        })
        .filter(Boolean) as IPanel[];
    },

    resizePanel(state, action: PayloadAction<{ workflowId: string; panelId: string; width: number }>) {
      const { workflowId, panelId, width } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels.map(panel => (panel.id === panelId ? { ...panel, width } : panel));
    },

    updateTabName(state, action: PayloadAction<{ workflowId: string; tabId: string; newName: string }>) {
      const { workflowId, tabId, newName } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels.map(panel => ({
        ...panel,
        tabs: panel.tabs.map(tab => (tab.id === tabId ? { ...tab, name: newName } : tab)),
      }));
    },

    updateTabFields(
      state,
      action: PayloadAction<{ workflowId: string; tabId: string; fields: Record<string, unknown> }>,
    ) {
      const { workflowId, tabId, fields } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels.map(panel => ({
        ...panel,
        tabs: panel.tabs.map(tab => (tab.id === tabId ? { ...tab, ...fields } : tab)),
      }));
    },

    updateTabLoading(state, action: PayloadAction<{ workflowId: string; tabId: string; isLoading: boolean }>) {
      const { workflowId, tabId, isLoading } = action.payload;
      const workflow = ensureWorkflow(state, workflowId);
      workflow.panels = workflow.panels.map(panel => ({
        ...panel,
        tabs: panel.tabs.map(tab => (tab.id === tabId ? { ...tab, isLoading } : tab)),
      }));
    },

    setActiveDragItem(state, action: PayloadAction<{ workflowId: string; item: { name: string } | null }>) {
      ensureWorkflow(state, action.payload.workflowId).activeDragItem = action.payload.item;
    },

    setTabDragSession(state, action: PayloadAction<{ workflowId: string; session: ITabDragSession | null }>) {
      ensureWorkflow(state, action.payload.workflowId).tabDragSession = action.payload.session;
    },

    setHoveredPanelId(state, action: PayloadAction<{ workflowId: string; panelId: string | null }>) {
      ensureWorkflow(state, action.payload.workflowId).hoveredPanelId = action.payload.panelId;
    },
  },
});

export default panelSlice.reducer;
export const panelActions = panelSlice.actions;

export const selectWorkflowPanels =
  <TTab extends IBaseTab = IBaseTab>(workflowId: string) =>
  (state: RootState): IPanel<TTab>[] =>
    (state.panelSlice.workflows[workflowId]?.panels ?? []) as IPanel<TTab>[];

export const selectWorkflowActiveDragItem =
  (workflowId: string) =>
  (state: RootState): { name: string } | null =>
    state.panelSlice.workflows[workflowId]?.activeDragItem ?? null;

export const selectWorkflowTabDragSession =
  (workflowId: string) =>
  (state: RootState): ITabDragSession | null =>
    state.panelSlice.workflows[workflowId]?.tabDragSession ?? null;

export const selectWorkflowHoveredPanelId =
  (workflowId: string) =>
  (state: RootState): string | null =>
    state.panelSlice.workflows[workflowId]?.hoveredPanelId ?? null;

export const selectWorkflowLastInteractedEditorDocumentId =
  (workflowId: string) =>
  (state: RootState): string | null =>
    state.panelSlice.workflows[workflowId]?.lastInteractedEditorDocumentId ?? null;

export const createPanelSelectors = <TTab extends IBaseTab = IBaseTab>(workflowId: string) => {
  const selectPanels = selectWorkflowPanels<TTab>(workflowId);
  return {
    selectPanels: createSelector(selectPanels, panels => panels),
    selectPanel: (state: RootState, panelId: string) => selectPanels(state).find(p => p.id === panelId),
    selectActiveTabId: (state: RootState, panelId: string) =>
      selectPanels(state).find(p => p.id === panelId)?.activeTabId ?? null,
    selectPanelTabs: (state: RootState, panelId: string) => selectPanels(state).find(p => p.id === panelId)?.tabs ?? [],
    selectIsTabActive: (state: RootState, tabId: string) => selectPanels(state).some(p => p.activeTabId === tabId),
    selectIsTabOpen: (state: RootState, tabId: string) =>
      selectPanels(state).some(p => p.tabs.some(t => t.id === tabId)),
    selectActiveDragItem: selectWorkflowActiveDragItem(workflowId),
    selectHoveredPanelId: selectWorkflowHoveredPanelId(workflowId),
  };
};
